
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Button, Grid,
  Chip, LinearProgress, IconButton, TextField, Divider, Paper
} from '@mui/material';
import { Delete, ShoppingCart, ArrowForward, ShoppingBag } from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cartAPI, transactionAPI } from '../services/api';

const MotionCard = motion(Card);

const CartPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');

  useEffect(() => { loadCart(); }, []);

  const loadCart = async () => {
    setLoading(true);
    try {
      const res = await cartAPI.getAll();
      setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await cartAPI.remove(itemId);
      setItems(items.filter(i => i.item_id !== itemId));
      toast.success('Removed from cart');
    } catch (err) { toast.error('Failed'); }
  };

  const totalPrice = items.reduce((sum, item) => sum + item.selling_price * item.quantity, 0);

  const handleCheckout = async () => {
    if (!shippingAddress.trim()) {
      toast.error('Enter a shipping address');
      return;
    }
    setCheckingOut(true);
    let success = 0;
    let failed = 0;

    for (const item of items) {
      try {
        await transactionAPI.create({
          item_id: item.item_id,
          quantity: item.quantity,
          offered_price: item.selling_price * item.quantity,
          shipping_address: shippingAddress,
        });
        success++;
      } catch (err) {
        failed++;
      }
    }

    if (success > 0) {
      await cartAPI.clear();
      toast.success(`${success} order(s) placed successfully!`);
      navigate('/orders');
    }
    if (failed > 0) {
      toast.error(`${failed} item(s) failed to order`);
    }
    setCheckingOut(false);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <ShoppingCart color="primary" />
          <Typography variant="h4" fontWeight={700}>Shopping Cart</Typography>
          <Chip label={`${items.length} items`} size="small" sx={{ ml: 1 }} />
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {items.length === 0 && !loading && (
          <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
            <ShoppingBag sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>Your cart is empty</Typography>
            <Button variant="contained" component={Link} to="/marketplace" size="small">
              Browse Marketplace
            </Button>
          </Card>
        )}

        {items.length > 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {items.map((item, i) => (
                  <MotionCard
                    key={item.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    sx={{ borderRadius: 3 }}
                  >
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>{item.item_name}</Typography>
                        <Chip label={item.category} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Qty: {item.quantity} | Available: {item.available_quantity}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" color="primary.main" fontWeight={700}>
                          ₹{(item.selling_price * item.quantity).toLocaleString()}
                        </Typography>
                        <IconButton color="error" onClick={() => handleRemove(item.item_id)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </MotionCard>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 3, position: 'sticky', top: 80 }} elevation={0}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Order Summary</Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">Items ({items.length})</Typography>
                  <Typography fontWeight={600}>₹{totalPrice.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography color="text.secondary">Shipping</Typography>
                  <Typography fontWeight={600} color="success.main">Free</Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight={700}>Total</Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    ₹{totalPrice.toLocaleString()}
                  </Typography>
                </Box>

                <TextField
                  fullWidth label="Shipping Address" multiline rows={3} sx={{ mb: 2 }}
                  value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="Enter your full delivery address..."
                />

                <Button
                  fullWidth variant="contained" size="large"
                  onClick={handleCheckout} disabled={checkingOut}
                  startIcon={<ShoppingBag />}
                >
                  {checkingOut ? 'Placing Orders...' : 'Place Order'}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CartPage;


